export interface RegistrationRequest {
  schoolUnitCode: string;
  className: string;
  studentCount: number;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  /** Sätts när användaren sett varningen om att klassen redan är anmäld och vill anmäla ändå. */
  confirmDuplicate?: boolean;
  /** Honeypot. Dold för människor, så ett värde här betyder en bot. */
  website?: string;
}

/**
 * Lärarlänken med nyckeln skickas bara i mejlet, så att anmälan inte går att bekräfta
 * utan att ha tillgång till brevlådan.
 */
export interface RegistrationResponse {
  /** Den publika klasslänken. Tom när den inte gick att bygga. */
  classLink: string;
  /** Url till danssidan. Tom när sidan saknas. */
  videoLink: string;
  /** False när anmälan sparades men mejlet med lärarlänken inte gick fram. */
  emailSent: boolean;
}

/** Fel som gick att tolka ur API-svaret, med den text som ska visas för användaren. */
export class RegistrationError extends Error {
  readonly status: number;
  /** Klassen finns redan på skolan. En varning som går att bekräfta, inte ett stopp. */
  readonly possibleDuplicate: boolean;

  constructor(message: string, status: number, possibleDuplicate = false) {
    super(message);
    this.name = "RegistrationError";
    this.status = status;
    this.possibleDuplicate = possibleDuplicate;
  }
}

const REGISTER_URL = "/breaks/api/registrations";

export async function registerClass(
  request: RegistrationRequest,
  signal?: AbortSignal,
): Promise<RegistrationResponse> {
  let response: Response;

  try {
    response = await fetch(REGISTER_URL, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(request),
    });
  } catch {
    // Nätverksfel ser annorlunda ut än ett felsvar och måste fångas separat.
    throw new RegistrationError(
      "Anmälan kunde inte skickas. Kontrollera din uppkoppling.",
      0,
    );
  }

  if (!response.ok) {
    const possibleDuplicate =
      response.status === 409 &&
      (
        await response
          .clone()
          .json()
          .catch(() => null)
      )?.possibleDuplicate === true;

    throw new RegistrationError(
      await readError(response),
      response.status,
      possibleDuplicate,
    );
  }

  return (await response.json()) as RegistrationResponse;
}

/**
 * Ber backend skicka nya klasslänkar till adressen. Svaret är detsamma oavsett om
 * adressen finns anmäld, så det enda som returneras är texten att visa.
 */
export async function resendClassLink(
  email: string,
  website: string,
): Promise<string> {
  let response: Response;

  try {
    response = await fetch(`${REGISTER_URL}/resend`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email, website }),
    });
  } catch {
    throw new RegistrationError(
      "Länken kunde inte skickas. Kontrollera din uppkoppling.",
      0,
    );
  }

  if (response.status === 429) {
    throw new RegistrationError(
      "För många försök. Vänta en stund och försök igen.",
      429,
    );
  }

  if (!response.ok) {
    throw new RegistrationError(await readError(response), response.status);
  }

  const body = await response.json();
  return typeof body?.message === "string"
    ? body.message
    : "Kolla din e-post. Har inget kommit fram om en stund, försök igen eller kontakta oss.";
}

/**
 * Plockar ut felmeddelandet ur svaret. Controllern svarar med { error } medan
 * modellvalidering ger ProblemDetails, så båda formerna hanteras.
 */
async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();

    if (typeof body?.error === "string") return body.error;
    if (typeof body?.title === "string") return body.title;
  } catch {
    // Svaret var inte JSON. Faller tillbaka på statuskoden nedan.
  }

  switch (response.status) {
    case 409:
      return "Klassen är redan anmäld.";
    case 410:
      return "Anmälan är inte öppen just nu.";
    case 404:
      return "Skolan hittades inte. Välj skola ur listan igen.";
    case 429:
      return "För många anmälningar från samma e-postadress. Försök igen om en stund.";
    default:
      return "Anmälan kunde inte sparas. Försök igen senare.";
  }
}
